import type { CreateDiscountPayload } from './useAdminBilling';
import { useState } from 'react';

type DiscountType = CreateDiscountPayload['type'];
type DiscountDuration = CreateDiscountPayload['duration'];

type DiscountFormState = {
  name: string;
  code: string;
  type: DiscountType;
  duration: DiscountDuration;
  durationInMonths: string;
  /** Percent off as typed, e.g. "12.5". Sent as basis points. */
  percent: string;
  /** Major units as typed, e.g. "25.00". Sent as minor units. */
  fixedAmount: string;
  maxRedemptions: string;
};

const EMPTY_FORM: DiscountFormState = {
  name: '',
  code: '',
  type: 'percentage',
  duration: 'once',
  durationInMonths: '',
  percent: '',
  fixedAmount: '',
  maxRedemptions: '',
};

const CODE_PATTERN = /^[A-Za-z0-9_-]{3,32}$/;

const isPositiveInteger = (value: number) => Number.isSafeInteger(value) && value > 0;

type BuildResult = { payload: CreateDiscountPayload; blockingReason?: undefined } | { blockingReason: string };

/**
 * The same rules discounts.ts's parseDiscountInput applies, checked here first
 * so the Create button can say what is wrong instead of the server's 400 doing
 * it after the fact. The payload is built in the server's own units: basis
 * points for a percentage, minor units of the billing currency for a fixed
 * amount (`amount`; the old `amounts: { usd }` object is gone).
 */
function buildPayload(form: DiscountFormState): BuildResult {
  const name = form.name.trim();
  if (!name) return { blockingReason: 'Enter a name' };

  const code = form.code.trim();
  if (code && !CODE_PATTERN.test(code)) {
    return { blockingReason: 'A code is 3 to 32 letters, digits, dashes or underscores' };
  }

  const durationInMonths = Number(form.durationInMonths);
  if (form.duration === 'repeating' && !(isPositiveInteger(durationInMonths) && durationInMonths <= 36)) {
    return { blockingReason: 'Enter the number of months, 1 to 36' };
  }

  const maxRedemptions = form.maxRedemptions.trim() ? Number(form.maxRedemptions) : undefined;
  if (maxRedemptions !== undefined && !isPositiveInteger(maxRedemptions)) {
    return { blockingReason: 'Max redemptions must be a whole number above zero' };
  }

  const common = {
    name,
    code: code || undefined,
    duration: form.duration,
    durationInMonths: form.duration === 'repeating' ? durationInMonths : undefined,
    maxRedemptions,
  };

  if (form.type === 'percentage') {
    const basisPoints = Math.round(Number(form.percent) * 100);
    if (!form.percent.trim() || !isPositiveInteger(basisPoints) || basisPoints > 10000) {
      return { blockingReason: 'Enter a percentage from 0.01 to 100' };
    }
    return { payload: { ...common, type: 'percentage', basisPoints } };
  }

  const amount = Math.round(Number(form.fixedAmount) * 100);
  if (!form.fixedAmount.trim() || !isPositiveInteger(amount)) {
    return { blockingReason: 'Enter an amount above zero' };
  }
  return { payload: { ...common, type: 'fixed', amount } };
}

type UseDiscountFormParams = { createDiscount: (payload: CreateDiscountPayload) => Promise<unknown> };

/** The Create discount dialog: open state, the fields, validation and submit. */
export const useDiscountForm = ({ createDiscount }: UseDiscountFormParams) => {
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState<DiscountFormState>(EMPTY_FORM);

  const setField = <K extends keyof DiscountFormState>(key: K, value: DiscountFormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const result = buildPayload(form);

  const open = () => {
    setForm(EMPTY_FORM);
    setIsOpen(true);
  };

  const close = () => {
    setForm(EMPTY_FORM);
    setIsOpen(false);
  };

  const submit = async () => {
    if (!('payload' in result) || !result.payload) return;
    try {
      await createDiscount(result.payload);
      close();
    } catch {
      // Toasted by the mutation; the dialog stays open with what was typed.
    }
  };

  return { isOpen, open, close, form, setField, blockingReason: result.blockingReason, submit };
};

export type DiscountFormManager = ReturnType<typeof useDiscountForm>;
